import apiClient, { ApiError } from './apiClient';
import { setSession, clearSession } from './session';

// Η **αρχική ενιαία υπηρεσία** της εφαρμογής: ταυτοποίηση, προφίλ, συλλογές και
// πόροι. Οι νεότερες οντότητες —κρίσεις, καθήκοντα, δραστηριότητα, κλειδιά,
// συνδρομές, χρήστες— έχουν **δικό τους** module. Ο διαχωρισμός έγινε ανά
// εργασία, ώστε παράλληλοι κλάδοι να μη συγκρούονται σε αυτό το αρχείο.
//
// Στο τέλος επιβιώνουν συναρτήσεις που απαντούν `NOT_IMPLEMENTED`, και η διάκρισή
// τους έχει σημασία: οι **τρεις** της επαναφοράς κωδικού είναι **ζωντανές** — τις
// καλούν δύο πραγματικές οθόνες και λείπουν οι διαδρομές του API (ΛΑ-01). Οι
// **τέσσερις** των καθηκόντων είναι **νεκρές**: τις αντικατέστησε πλήρως το
// `tasks.service.js` και δεν τις καλεί κανείς.

export const userService = {
    login, logout, register,
    getCurrentUser, updateCurrentUser, getInterestSuggestions,
    resetPassword, validateResetToken, setNewPassword,
    getCollections, createCollection, getCollection, updateCollection, deleteCollection,
    getCollectionRemovalPreview,
    archiveCollection, unarchiveCollection,
    addCollectionMember, updateCollectionMemberRole, removeCollectionMember,
    getCollectionResources, getCollectionResource, searchResources,
    relateResource, unrelateResource,
    createResource, updateResource, deleteResource, fetchUrlMetadata,
    getTasks, addTask, editTask, deleteTask
};

// --- Λειτουργίες που περιμένουν επόμενες εργασίες -------------------------------

function pendingRead(name, value) {
    if (import.meta.env.DEV) console.warn(`[P1] ${name}: η διαδρομή δεν υπάρχει ακόμη`);
    return Promise.resolve(value);
}

function pendingWrite(name) {
    if (import.meta.env.DEV) console.warn(`[P1] ${name}: η διαδρομή δεν υπάρχει ακόμη`);
    return Promise.reject(new ApiError({
        status: 501,
        code: 'NOT_IMPLEMENTED',
        message: 'This feature is not available yet'
    }));
}

// --- Ταυτοποίηση και συνεδρία --------------------------------------------------

async function login(email, password) {
    const { token, user } = await apiClient.post('/api/auth/login', { email, password }, { auth: false });
    setSession(token, user);
    return user;
}

// Η αποσύνδεση είναι τοπική. Η επικοινωνία είναι χωρίς κατάσταση (§4.2) και το
// διακριτικό δεν καταγράφεται στη βάση, οπότε δεν υπάρχει τι να ανακληθεί.
function logout() {
    clearSession();
}

// Το API επιστρέφει διακριτικό και στην εγγραφή. Δεν αποθηκεύεται: η οθόνη
// σύνδεσης παραμένει το μοναδικό σημείο που ανοίγει συνεδρία.
async function register(data) {
    await apiClient.post('/api/auth/register', {
        username: data.username,
        email: data.email,
        password: data.password,
        fullName: data.fullName
    }, { auth: false });
}

// --- Προφίλ --------------------------------------------------------------------

function getCurrentUser() {
    return apiClient.get('/api/auth/me');
}

function updateCurrentUser(data) {
    return apiClient.patch('/api/auth/me', {
        fullName: data.fullName,
        avatar: data.avatar,
        interests: data.interests
    });
}

// Τα ενδιαφέροντα που υπάρχουν ήδη στο σύστημα, ως προτάσεις κατά τη συμπλήρωση
// του προφίλ. Επιστρέφονται μόνο οι τιμές, ποτέ ποιος τις δήλωσε.
function getInterestSuggestions() {
    return apiClient.get('/api/interests');
}

// --- Συλλογές (ΛΑ-04 έως ΛΑ-07) ------------------------------------------------

// ΛΑ-06 και ΛΑ-07. Χωρίς παράμετρο το API επιστρέφει **μόνο τις ενεργές**, οπότε οι
// αρχειοθετημένες ήταν απρόσιτες από τη διεπαφή: η ΛΑ-07 απαιτεί αρχειοθέτηση **με
// επαναφορά**, και μια συλλογή που δεν εμφανίζεται πουθενά δεν μπορεί να επανέλθει.
//
// Το `query` είναι η σειριοποιημένη κατάσταση περιήγησης (`browseState.js`):
// αναζήτηση, ταξινόμηση και σελίδα ταξιδεύουν μαζί, γιατί το φιλτράρισμα έφυγε
// από τον πελάτη (P1-5). Η υπογραφή δέχεται και σκέτο `{ page }` όπως πριν.
function getCollections({ archived = false, scope = 'mine', query = '' } = {}) {
    const params = new URLSearchParams(query);
    params.set('scope', scope);
    // Στην ανακάλυψη ο διακομιστής αγνοεί την αρχειοθέτηση ούτως ή άλλως, αλλά η
    // παράμετρος δεν στέλνεται καθόλου: αίτημα που ζητά κάτι το οποίο θα αγνοηθεί
    // διαβάζεται αργότερα ως δυνατότητα που υπάρχει.
    if (scope !== 'public') params.set('archived', archived ? 'true' : 'false');
    return apiClient.getPage(`/api/collections?${params.toString()}`);
}

function createCollection(data) {
    return apiClient.post('/api/collections', {
        title: data.title,
        description: data.description,
        topic: data.topic,
        visibility: data.visibility
    });
}

function getCollection(id) {
    return apiClient.get(`/api/collections/${id}`);
}

function updateCollection(id, data) {
    return apiClient.patch(`/api/collections/${id}`, data);
}

/**
 * ΜΛΑ-03: τα πλήθη που θα χαθούν, **τη στιγμή που ρωτά ο χρήστης**.
 *
 * Καλείται όταν **ανοίγει** ο διάλογος και όχι όταν φορτώνει η σελίδα. Η διαφορά
 * δεν είναι απόδοση: πλήθη της φόρτωσης είναι όσο παλιά είναι εκείνη, και ένας
 * συνεργάτης που πρόσθεσε δέκα πόρους στο μεταξύ δεν φαίνεται. Ψευδής αριθμός σε
 * μη αναστρέψιμη ενέργεια είναι χειρότερος από κανέναν, γιατί ο χρήστης τον
 * εμπιστεύεται.
 */
function getCollectionRemovalPreview(id) {
    return apiClient.get(`/api/collections/${id}/removal-preview`);
}

function deleteCollection(id) {
    return apiClient.del(`/api/collections/${id}`);
}

function archiveCollection(id) {
    return apiClient.post(`/api/collections/${id}/archive`);
}

function unarchiveCollection(id) {
    return apiClient.post(`/api/collections/${id}/unarchive`);
}

function addCollectionMember(id, username, role) {
    return apiClient.post(`/api/collections/${id}/members`, { username, role });
}

function updateCollectionMemberRole(id, userId, role) {
    return apiClient.patch(`/api/collections/${id}/members/${userId}`, { role });
}

function removeCollectionMember(id, userId) {
    return apiClient.del(`/api/collections/${id}/members/${userId}`);
}

// --- Ψηφιακοί πόροι (ΛΑ-08 έως ΛΑ-13) ------------------------------------------

// Σελιδοποιημένο: το API στέλνει 20 ανά σελίδα και το `meta` λέει πόσα υπάρχουν.
// Χωρίς αυτό η διεπαφή έδειχνε τους πρώτους είκοσι και οι υπόλοιποι ήταν αθέατοι.
//
// Το `query` φέρνει αναζήτηση, ετικέτες, συντάκτη, ημερομηνίες και ταξινόμηση
// (ΛΑ-14, ΛΑ-15). Ο έλεγχος γίνεται στον διακομιστή, οπότε το `meta.total`
// αφορά το **φιλτραρισμένο** σύνολο και τα μηνύματα της οθόνης είναι αληθή.
/**
 * ΛΑ-12: δήλωση σχέσης ανάμεσα σε δύο πόρους, **μέσα στο πλαίσιο της συλλογής**.
 *
 * Το πλαίσιο δεν είναι διακοσμητικό: ο πόρος είναι κοινός (D9), οπότε χωρίς αυτό
 * ο εξυπηρετητής δεν ξέρει σε ποια συλλογή έγινε η ενέργεια και το γεγονός θα
 * εκπεμπόταν με μία τυχαία από τις συλλογές του.
 */
function relateResource(collectionId, resourceId, relatedId) {
    return apiClient.post(`/api/collections/${collectionId}/resources/${resourceId}/related`, {
        resource: relatedId
    });
}

/** Αφαίρεση της σχέσης. Ο εξυπηρετητής τη σβήνει από όποια πλευρά κι αν γράφτηκε. */
function unrelateResource(collectionId, resourceId, relatedId) {
    return apiClient.del(
        `/api/collections/${collectionId}/resources/${resourceId}/related/${relatedId}`
    );
}

function getCollectionResources(collectionId, { query = '' } = {}) {
    const suffix = query ? `?${query}` : '';
    return apiClient.getPage(`/api/collections/${collectionId}/resources${suffix}`);
}

/**
 * Ένας πόρος **μέσα στο πλαίσιο της συλλογής**: ο μόνιμος σύνδεσμος (P1-5).
 *
 * Δεν χρησιμοποιείται το `GET /api/resources/:id`, και η διαφορά δεν είναι
 * αισθητική. Ο πόρος μπορεί να είναι σε άλλη σελίδα, οπότε ο πελάτης δεν τον
 * έχει· και ο έλεγχος «ανήκει σε αυτή τη συλλογή και επιτρέπεται να τον δω;»
 * πρέπει να γίνει **στο API**, με τον ίδιο φράχτη που φυλάει τη σελίδα της
 * συλλογής. Κανόνας που θα ζούσε εδώ, θα ίσχυε για αυτόν τον πελάτη.
 */
function getCollectionResource(collectionId, resourceId) {
    return apiClient.get(`/api/collections/${collectionId}/resources/${resourceId}`);
}

/** ΛΑ-14: αναζήτηση σε **όλες** τις ορατές συλλογές. */
function searchResources(query = '') {
    const suffix = query ? `?${query}` : '';
    return apiClient.getPage(`/api/resources${suffix}`);
}

/**
 * ΛΑ-09 από το πόρταλ (Απόφαση D12): τα μεταδεδομένα μιας διεύθυνσης.
 *
 * `POST` και όχι `GET`: η διεύθυνση-στόχος δεν μπαίνει σε logs ούτε σε κρυφές
 * μνήμες. Το αίτημα το κάνει ο **εξυπηρετητής**, με τις δικλείδες της D12 — ο
 * πελάτης δεν θα μπορούσε ούτως ή άλλως, γιατί το CORS δεν του επιτρέπει να
 * διαβάσει ξένη προέλευση.
 */
function fetchUrlMetadata(url) {
    return apiClient.post('/api/resources/metadata', { url });
}

/**
 * ΛΑ-08. Επιστρέφει **και το `meta`**, γιατί η έκβαση δεν είναι μία.
 *
 * Με την Απόφαση D9 το `POST` έχει τρεις εκβάσεις: `201` όταν δημιουργείται πόρος,
 * `200` με `meta.attached` όταν η διεύθυνση υπάρχει ήδη αλλού και η συλλογή
 * **προσαρτάται** σε υπάρχον έγγραφο, και `409` όταν υπάρχει ήδη σε αυτή τη
 * συλλογή. Χωρίς το `meta`, η διεπαφή θα έλεγε «δημιουργήθηκε» και στις δύο
 * πρώτες, που είναι ανακριβές στη δεύτερη (ΜΛΑ-03).
 */
function createResource(collectionId, data) {
    return apiClient.post(`/api/collections/${collectionId}/resources`, {
        url: data.url,
        title: data.title,
        description: data.description,
        // Κενό πεδίο σημαίνει «καμία εικόνα», δηλαδή `null`. Το κενό αλφαριθμητικό
        // δεν είναι έγκυρη διεύθυνση και θα απορριπτόταν με 422 από το `httpUrl`.
        image: data.image ? data.image : null,
        tags: data.tags,
        funding: data.funding
    }, { raw: true }).then((payload) => ({
        data: payload?.data ?? null,
        attached: payload?.meta?.attached === true,
        // P1-5β: ό,τι έστειλε ο χρήστης και δεν αποθηκεύτηκε, πεδίο προς πεδίο.
        // **Πάντοτε πίνακας**, ώστε ο καλών να μη χρειάζεται να ξεχωρίσει «δεν
        // χάθηκε τίποτα» από «δεν ρωτήθηκε»: το πρώτο είναι κενός πίνακας, το
        // δεύτερο δεν συμβαίνει σε αυτή τη διαδρομή.
        discarded: Array.isArray(payload?.meta?.discarded) ? payload.meta.discarded : []
    }));
}

/**
 * Ενημέρωση πόρου **μέσα σε μια συγκεκριμένη συλλογή** (Μ1 του P2-1).
 *
 * Η διαδρομή είναι υπο-πόρος της συλλογής, όπως η διαγραφή και τα μέλη, και ο
 * λόγος είναι ο ίδιος με την Απόφαση D9: ο πόρος είναι κοινός, οπότε το
 * `PATCH /api/resources/:id` **δεν ξέρει σε ποια συλλογή έγινε η ενέργεια** και
 * δεν επιτρέπεται να τη μαντέψει από το `collections[]`. Χωρίς το πλαίσιο ο
 * εξυπηρετητής δεν μπορεί να εκπέμψει `resource.updated`: μια συνδρομή webhook
 * της συλλογής Β θα έπαιρνε γεγονός για ενέργεια που έγινε στην Α.
 *
 * Πριν από τη σύνδεση αυτή, η επεξεργασία από το πόρταλ **δεν εξέπεμπε τίποτα**.
 * Η παλιά διαδρομή παραμένει στο API για συμβατότητα και δεν καλείται από τη
 * διεπαφή, ακριβώς όπως το `DELETE /api/resources/:id` παρακάτω.
 */
function updateResource(collectionId, resourceId, data) {
    return apiClient.patch(`/api/collections/${collectionId}/resources/${resourceId}`, {
        ...data,
        // Ίδια κανονικοποίηση με την `createResource`: κενό πεδίο σημαίνει «καμία
        // εικόνα», δηλαδή `null`. Το κενό αλφαριθμητικό **δεν είναι** έγκυρη
        // διεύθυνση και το `.strict()` του σχήματος το απορρίπτει με 422.
        //
        // Έλειπε, και η συνέπεια ήταν ότι **κάθε πόρος χωρίς εικόνα ήταν αδύνατο να
        // επεξεργαστεί**: η φόρμα αρχικοποιεί το πεδίο σε `''`, οπότε η αποθήκευση
        // γύριζε 422 με σφάλμα στο `image` — σε πόρο που ο χρήστης δεν είχε καν
        // αγγίξει την εικόνα. Ίσχυε και στην παλιά contextless διαδρομή, με
        // πανομοιότυπο σχήμα, οπότε δεν το εισήγαγε η σύνδεση της Α3γ.
        image: data.image ? data.image : null
    });
}

/**
 * Αφαίρεση πόρου **από μία συγκεκριμένη συλλογή** (Απόφαση D9).
 *
 * Η διαδρομή είναι υπο-πόρος της συλλογής, όπως και τα μέλη. Το `DELETE
 * /api/resources/:id` εξακολουθεί να υπάρχει στο API με τη σημασία «αφαίρεση από
 * **όλες** τις συλλογές όπου έχω δικαίωμα εγγραφής», αλλά **δεν καλείται από
 * πουθενά στη διεπαφή**: ο χρήστης που πατά διαγραφή βλέπει μία συλλογή και
 * εννοεί αυτήν. Με τον κοινό πόρο της D9, η κλήση χωρίς πλαίσιο θα έσβηνε την
 * καταχώρηση και από κάθε άλλη συλλογή του ίδιου χρήστη.
 */
function deleteResource(collectionId, resourceId) {
    return apiClient.del(`/api/collections/${collectionId}/resources/${resourceId}`);
}

// --- Επαναφορά κωδικού (ΛΑ-01, P1-9) -------------------------------------------

/**
 * Αίτημα επαναφοράς.
 *
 * ⚠ **Η απόκριση είναι η ίδια, υπαρκτή ή όχι η διεύθυνση**, και η οθόνη **δεν
 * επιτρέπεται να το χαλάσει**: μήνυμα «δεν βρέθηκε λογαριασμός» θα ξανάφτιαχνε
 * στον πελάτη την απαρίθμηση που ο εξυπηρετητής φρόντισε να μην υπάρχει.
 */
function resetPassword(email) {
    return apiClient.post('/api/auth/forgot', { email }, { auth: false });
}

/**
 * **ΔΕΝ ΡΩΤΑΕΙ ΤΟΝ ΕΞΥΠΗΡΕΤΗΤΗ, ΚΑΙ ΕΙΝΑΙ ΑΠΟΦΑΣΗ.**
 *
 * Η οθόνη ήταν γραμμένη για διαδρομή που **επαληθεύει** το διακριτικό πριν
 * εμφανίσει τη φόρμα, και επιστρέφει τον χρήστη στον οποίο ανήκει. Τέτοια
 * διαδρομή **δεν φτιάχτηκε**, για δύο λόγους:
 *
 * 1. **Θα ήταν τρίτη διαδρομή εκτός του πίνακα του §4.5.1**, για ευκολία και όχι
 *    για λειτουργία: ο έλεγχος γίνεται ούτως ή άλλως στην υποβολή.
 * 2. **Θα επέστρεφε ταυτότητα λογαριασμού σε ανώνυμο αίτημα.** Όποιος κρατά τον
 *    σύνδεσμο μπορεί ήδη να αλλάξει τον κωδικό, οπότε δεν είναι διαρροή — αλλά
 *    είναι περιττή έκθεση, και η επιπλέον οθόνη δεν αξίζει το ερώτημα.
 *
 * Το τίμημα, δηλωμένο: **ληγμένος σύνδεσμος δείχνει τη φόρμα** και αποτυγχάνει
 * στην υποβολή, αντί να το πει από την αρχή. Το μήνυμα είναι το ίδιο και στις δύο
 * περιπτώσεις, οπότε ο χρήστης μαθαίνει το ίδιο πράγμα μία οθόνη αργότερα.
 */
function validateResetToken(token) {
    return Promise.resolve({ user: token ? { id: null } : null });
}

/** Ορισμός νέου κωδικού. Το **διακριτικό** ταξιδεύει, όχι ο χρήστης. */
function setNewPassword(token, password) {
    return apiClient.post('/api/auth/reset', { token, password }, { auth: false });
}

// --- Καθήκοντα (P1-4) ----------------------------------------------------------

function getTasks() {
    return pendingRead('getTasks', { todos: [] });
}

function addTask() {
    return pendingWrite('addTask');
}

function editTask() {
    return pendingWrite('editTask');
}

function deleteTask() {
    return pendingWrite('deleteTask');
}

export default userService;
